import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAlert } from "../components/AdminAlert";
import { InfoTooltip } from "../components/InfoTooltip";
import { useApi } from "../hooks/useApi";
import {
  ApiError,
  type SalesforceOrg,
  type SalesforceOrgInput,
  type SalesforceOrgPatch,
  api,
} from "../lib/api";

interface DraftOrg {
  alias: string;
  display_name: string;
  login_url: string;
  client_id: string;
  scopes: string;
  enabled: boolean;
}

const SF_LOGIN_PROD = "https://login.salesforce.com";
const SF_LOGIN_SANDBOX = "https://test.salesforce.com";
const DEFAULT_SCOPES = "api, refresh_token, id";

function emptyDraft(): DraftOrg {
  return {
    alias: "",
    display_name: "",
    login_url: SF_LOGIN_PROD,
    client_id: "",
    scopes: DEFAULT_SCOPES,
    enabled: true,
  };
}

function draftFromOrg(org: SalesforceOrg): DraftOrg {
  return {
    alias: org.alias,
    display_name: org.display_name,
    login_url: org.login_url,
    client_id: org.client_id,
    scopes: org.scopes.join(", "),
    enabled: org.enabled,
  };
}

function parseScopes(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

export function AdminSalesforce(): JSX.Element {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const data = useApi(() => api.admin.salesforce.orgs(), [refresh]);
  const [editing, setEditing] = useState<"new" | string | null>(null);
  const [draft, setDraft] = useState<DraftOrg>(emptyDraft());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = (): void => setRefresh((r) => r + 1);

  const startNew = (): void => {
    setEditing("new");
    setDraft(emptyDraft());
    setError(null);
    setNotice(null);
  };

  const startEdit = (org: SalesforceOrg): void => {
    setEditing(org.alias);
    setDraft(draftFromOrg(org));
    setError(null);
    setNotice(null);
  };

  const cancelEdit = (): void => {
    setEditing(null);
    setError(null);
  };

  const onSave = async (): Promise<void> => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (editing === "new") {
        const input: SalesforceOrgInput = {
          alias: draft.alias.trim(),
          display_name: draft.display_name.trim(),
          login_url: draft.login_url.trim(),
          client_id: draft.client_id.trim(),
          scopes: parseScopes(draft.scopes),
          enabled: draft.enabled,
        };
        await api.admin.salesforce.create(input);
      } else if (typeof editing === "string") {
        const patch: SalesforceOrgPatch = {
          display_name: draft.display_name.trim(),
          login_url: draft.login_url.trim(),
          client_id: draft.client_id.trim(),
          scopes: parseScopes(draft.scopes),
          enabled: draft.enabled,
        };
        await api.admin.salesforce.update(editing, patch);
      }
      setEditing(null);
      setNotice(t("admin.sf.saved"));
      reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (alias: string): Promise<void> => {
    if (!window.confirm(t("admin.sf.confirmDelete", { alias }))) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await api.admin.salesforce.delete(alias);
      setNotice(t("admin.sf.deleted", { alias }));
      reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (data.status === "loading") {
    return <p className="launchpad__hint">{t("admin.loading")}</p>;
  }
  if (data.status === "error") {
    const msg = data.error instanceof ApiError ? data.error.message : data.error.message;
    return <p className="launchpad__hint launchpad__hint--error">{msg}</p>;
  }

  return (
    <section className="admin">
      <div className="admin__header">
        <h2>{t("admin.sf.title")}</h2>
        <button type="button" onClick={startNew} disabled={editing !== null}>
          {t("admin.sf.newOrg")}
        </button>
      </div>

      <p className="launchpad__hint">{t("admin.sf.intro")}</p>

      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      {notice ? <AdminAlert kind="success">{notice}</AdminAlert> : null}

      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.sf.col.alias")}</th>
            <th>{t("admin.sf.col.displayName")}</th>
            <th>{t("admin.sf.col.loginUrl")}</th>
            <th>{t("admin.sf.col.scopes")}</th>
            <th>{t("admin.sf.col.enabled")}</th>
            <th>{t("admin.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data.data.orgs.map((org) => (
            <tr key={org.alias}>
              <td>
                <code>{org.alias}</code>
              </td>
              <td>{org.display_name}</td>
              <td>
                <code>{org.login_url}</code>
              </td>
              <td>{org.scopes.join(", ")}</td>
              <td>
                {org.enabled ? (
                  <span className="badge">{t("admin.sf.enabledYes")}</span>
                ) : (
                  <span className="badge">{t("admin.sf.enabledNo")}</span>
                )}
              </td>
              <td className="admin__actions">
                <button type="button" onClick={() => startEdit(org)} disabled={editing !== null}>
                  {t("admin.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => void onDelete(org.alias)}
                  disabled={editing !== null || busy}
                >
                  {t("admin.delete")}
                </button>
              </td>
            </tr>
          ))}
          {data.data.orgs.length === 0 ? (
            <tr>
              <td colSpan={6} className="admin__empty">
                {t("admin.sf.empty")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {editing !== null ? (
        <form
          className="admin__form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
        >
          <h3>
            {editing === "new" ? t("admin.sf.formNew") : t("admin.sf.formEdit", { alias: editing })}
          </h3>
          <p className="form__req-note">{t("common.requiredHint")}</p>

          {editing === "new" ? (
            <div className="admin__field">
              <span className="field-head">
                <label htmlFor="sf_alias">
                  {t("admin.sf.field.alias")}
                  <span className="field-req" aria-hidden="true">
                    *
                  </span>
                </label>
                <InfoTooltip text={t("admin.sf.field.aliasHint")} />
              </span>
              <input
                id="sf_alias"
                type="text"
                value={draft.alias}
                onChange={(e) => setDraft({ ...draft, alias: e.target.value })}
                placeholder="prod"
                required
                pattern="[a-z][a-z0-9-]*[a-z0-9]|[a-z]"
              />
            </div>
          ) : null}

          <div className="admin__field">
            <label htmlFor="sf_display_name">
              {t("admin.sf.field.displayName")}
              <span className="field-req" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="sf_display_name"
              type="text"
              value={draft.display_name}
              onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
              required
            />
          </div>

          <div className="admin__field">
            <label htmlFor="sf_login_url">
              {t("admin.sf.field.loginUrl")}
              <span className="field-req" aria-hidden="true">
                *
              </span>
            </label>
            <select
              aria-label={t("admin.sf.field.loginUrl")}
              value={
                draft.login_url === SF_LOGIN_PROD || draft.login_url === SF_LOGIN_SANDBOX
                  ? draft.login_url
                  : "custom"
              }
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") {
                  return;
                }
                setDraft({ ...draft, login_url: v });
              }}
            >
              <option value={SF_LOGIN_PROD}>Production ({SF_LOGIN_PROD})</option>
              <option value={SF_LOGIN_SANDBOX}>Sandbox ({SF_LOGIN_SANDBOX})</option>
              <option value="custom">{t("admin.sf.field.loginUrlCustom")}</option>
            </select>
            <input
              id="sf_login_url"
              type="url"
              value={draft.login_url}
              onChange={(e) => setDraft({ ...draft, login_url: e.target.value })}
              required
              pattern="https://.+"
            />
          </div>

          <div className="admin__field">
            <span className="field-head">
              <label htmlFor="sf_client_id">
                {t("admin.sf.field.clientId")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
              <InfoTooltip text={t("admin.sf.field.clientIdHint")} />
            </span>
            <input
              id="sf_client_id"
              type="text"
              value={draft.client_id}
              onChange={(e) => setDraft({ ...draft, client_id: e.target.value })}
              required
            />
          </div>

          <div className="admin__field">
            <span className="field-head">
              <label htmlFor="sf_scopes">
                {t("admin.sf.field.scopes")}
                <span className="field-req" aria-hidden="true">
                  *
                </span>
              </label>
              <InfoTooltip text={t("admin.sf.field.scopesHint")} />
            </span>
            <input
              id="sf_scopes"
              type="text"
              value={draft.scopes}
              onChange={(e) => setDraft({ ...draft, scopes: e.target.value })}
              placeholder={DEFAULT_SCOPES}
              required
            />
          </div>

          <label className="admin__checkbox">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
            {t("admin.sf.field.enabled")}
          </label>

          {busy ? (
            <output className="admin__busy" aria-live="polite">
              <span className="spinner" aria-hidden="true" />
              {t("common.saving")}
            </output>
          ) : null}
          <div className="admin__actions">
            <button type="submit" className="primary" disabled={busy}>
              {t("admin.save")}
            </button>
            <button type="button" onClick={cancelEdit} disabled={busy}>
              {t("admin.cancel")}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 409) return e.detail ?? e.message;
    if (e.status === 422) return e.detail ?? e.message;
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
